import { Box, Button, Flex, SimpleGrid, Text, useToast, VStack } from '@chakra-ui/react'
import { Fragment, FunctionComponent, useEffect, useState } from 'react'
import NewPatientModal from './NewPatientModal'
import UpdatePatientModal from './UpdatePatientModal'

import { Patient } from '../../types'
import PatientTable from './PatientTable'
import { ImportPatientsModal } from '../../features/patient/components/ImportPatientModal'
import { RootState } from '../../app/store'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { PrescriberSelector } from '../../components/PrescriberSelector'
import { LimitedPrescriberProfile } from '../../features/users/UsersSlice'
import UnauthorizedPage from '../../components/UnauthorizedPage'
import { Permission } from '../../types/permission'
import { selectPatient } from '../../features/patient/PatientContextSlice'
import { patientsApi } from '../../services/patients'
import { useNavigate } from 'react-router-dom'
import { routes } from '../routes'
import { resetAllValidationResults } from './PatientFormSlice'
import { useHasEntitlement } from '../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'
import { SubscriptionNotificationModal } from '../Billing/SubscriptionNotificationModal'
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard'
import { useCanPrescribeFor } from '../../features/ehr-launch/useCanPrescribeFor'
import { analyticsAction } from '../../common/utils/analytics'

const PatientPage: FunctionComponent<unknown> = () => {
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const {
    isOrgRegistrationAndPaymentFeatureOn,
    isSubscriptionNotificationOpen,
    closeSubscriptionNotification,
    runWithValidSubscription,
  } = useSubscriptionGuard()
  const prescriber = useAppSelector(({ prescriber }: RootState) => prescriber?.prescriber)
  const users = useAppSelector((state: RootState) => state.users?.data)

  const prescribers = users?.filter((user) => user.prescriber) ?? []
  const isPatientAdmin = useAppSelector((state: RootState) =>
    state.authentication.permissions?.includes(Permission.PATIENT_MANAGEMENT)
  )
  const isSharedPatientsFeatureOn = useHasEntitlement(FeatureKey.SHARED_PATIENTS)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const toast = useToast()
  const canPrescribeFor = useCanPrescribeFor()

  const handlePrescribe = (patient: Patient) => {
    if (!canPrescribeFor(patient.id)) {
      toast({
        title: 'This patient is not the active EHR patient',
        description: 'Select the patient in the EHR and begin a new launch to prescribe for them.',
        status: 'warning',
        isClosable: true,
      })
      return
    }
    dispatch(selectPatient(patient))
    dispatch(patientsApi.util.resetApiState())
    navigate(routes.newScript.path)
  }

  const currentPrescriber =
    (prescriber?.active || !isPatientAdmin
      ? (prescriber as LimitedPrescriberProfile)
      : prescribers.find((u) => u.prescriber?.active)?.prescriber) ?? null

  const [selectedPrescriber, setSelectedPrescriber] = useState<LimitedPrescriberProfile | null>(
    currentPrescriber
  )

  useEffect(() => {
    setSelectedPrescriber(currentPrescriber)
  }, [currentPrescriber])

  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isAbleToManagePatient = !!organizationSettings?.enableManagePatient
  const isSelectedPrescriberInactive = !selectedPrescriber?.active

  if (!isPatientAdmin && !prescriber) {
    return <UnauthorizedPage />
  }

  return (
    <Box minWidth={0} width={'100%'} height={['100%', '100vh']}>
      <VStack p="12px" alignItems="stretch" spacing={6} height="100%">
        <SimpleGrid
          columns={{ base: 1, xl: 2 }}
          spacing="4"
          gridTemplateColumns={{ base: '1fr', xl: '1fr auto' }}
        >
          <Flex gap={4} flexDirection={['column', 'row']}>
            <Text as="h1" fontSize="3xl" fontWeight="bold">
              Patients
            </Text>
            {isAbleToManagePatient && (
              <Fragment>
                <NewPatientModal
                  selectedPrescriber={selectedPrescriber}
                  onPrescriberChange={(id) => {
                    const prescriber =
                      prescribers.find((u) => u.prescriber?.id === id)?.prescriber || null
                    setSelectedPrescriber(prescriber)
                  }}
                  hidePrescriberSelection={!isPatientAdmin}
                >
                  {({ openModal }) => (
                    <Button
                      colorScheme="blue"
                      onClick={() => {
                        runWithValidSubscription(() => {
                          dispatch(resetAllValidationResults())
                          openModal()
                        })
                      }}
                      disabled={!isPatientAdmin && isSelectedPrescriberInactive}
                      {...analyticsAction('patients_new_patient')}
                    >
                      + New Patient
                    </Button>
                  )}
                </NewPatientModal>
                <ImportPatientsModal
                  selectedPrescriber={selectedPrescriber}
                  onPrescriberChange={(id) =>
                    setSelectedPrescriber(
                      prescribers.find((u) => u.prescriber?.id === id)?.prescriber ?? null
                    )
                  }
                  hidePrescriberSelection={!isPatientAdmin}
                >
                  {({ openModal }) => (
                    <Button
                      colorScheme="blue"
                      onClick={() => runWithValidSubscription(openModal)}
                      disabled={!isPatientAdmin && isSelectedPrescriberInactive}
                      {...analyticsAction('patients_import')}
                    >
                      Import Patients
                    </Button>
                  )}
                </ImportPatientsModal>
              </Fragment>
            )}
          </Flex>
          {isPatientAdmin && !isSharedPatientsFeatureOn && (
            <Flex pr={10} gap={4} flexDirection={['column', 'row']}>
              <Text as={'b'} fontSize={'xl'}>
                Selected prescriber:
              </Text>
              <PrescriberSelector
                users={prescribers}
                selectedPrescriberId={selectedPrescriber?.id}
                onSelect={(id) =>
                  setSelectedPrescriber(
                    prescribers.find((u) => u.prescriber?.id === id)?.prescriber ?? null
                  )
                }
              />
            </Flex>
          )}
        </SimpleGrid>

        {prescribers.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
            width="100%"
            borderWidth="1px"
            borderRadius="lg"
            p={4}
          >
            <Text fontSize="lg" color="gray.500">
              No prescribers available in this organisation.
            </Text>
          </Box>
        ) : (
          <PatientTable
            setSelectedPatient={setSelectedPatient}
            isAbleToManagePatient={
              isAbleToManagePatient && (isPatientAdmin || !isSelectedPrescriberInactive)
            }
            selectedPrescriber={selectedPrescriber}
            prescribe={handlePrescribe}
          />
        )}
      </VStack>

      {selectedPatient && (
        <UpdatePatientModal
          patient={selectedPatient}
          isOpen={true}
          onClose={() => setSelectedPatient(null)}
          selectedPrescriber={selectedPrescriber}
          isAbleToCreateOrDelete={!isSelectedPrescriberInactive}
        />
      )}
      {isOrgRegistrationAndPaymentFeatureOn && (
        <SubscriptionNotificationModal
          isOpen={isSubscriptionNotificationOpen}
          onClose={closeSubscriptionNotification}
        />
      )}
    </Box>
  )
}

export default PatientPage
